import { BaseProcessor, ColumnMetaData } from './BaseProcessor';
import * as Highcharts from 'highcharts';

export class StackBar extends BaseProcessor {
  protected override chartType: string = 'column';

  constructor(
    data: { [key: string]: any }[],
    xAxis: string[],
    yAxis: string[],
    groupBy: string[],
    columnsMetaData: { [key: string]: ColumnMetaData } = {},
    options: any = {}
  ) {
    super();
    this.data = data;
    this.xAxis = xAxis;
    this.yAxis = yAxis;
    this.groupBy = groupBy;
    this.columnsMetaData = columnsMetaData;
    this.options = options;
    this.addChartTypeToOptions();
  }

  addToolTip() {
    this.options.tooltip = {
      formatter: function () {
        const formatNumber = (val: any) =>
          typeof val === 'number'
            ? val.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : val;

        const yMetaData = this.point?.series?.userOptions?.yMetaData;
        const xMetaData = this.point?.series?.userOptions?.xMetaData;
        let keyString = this.key;
        if (xMetaData.type === 'datetime' || xMetaData.type === 'date') {
          keyString = Highcharts.dateFormat('%Y-%m-%d', this.x);
        } else if (xMetaData.type === 'month') {
          keyString = Highcharts.dateFormat('%b %Y', this.x);
        } else if (xMetaData.type === 'year') {
          keyString = Highcharts.dateFormat('%Y', this.x);
        }

        return `<b>
          ${keyString} 
          </b><br/>
          ${this.series.name}
          : <b>
          ${yMetaData?.unit || ''}${formatNumber(this.y)}
          </b><br/>
          Total: <b>
          ${yMetaData?.unit || ''}${formatNumber(this.point.stackTotal)}
          </b>`;
      },
    };
    if (this.isDateTime(this.columnsMetaData[this.xAxis[0]].type)) {
      this.options.tooltip = this.options.tooltip || {};
      this.options.tooltip.xDateFormat = this.getToolTipDateFormat(
        this.columnsMetaData[this.xAxis[0]].type
      );
    }
  }

  protected addPlotOptions() {
    this.options.plotOptions = {
      column: {
        stacking: 'normal',
      },
    };
  }

  addXAxisData() {
    if (this.xAxis.length > 0) {
      if (this.isDateTime(this.columnsMetaData[this.xAxis[0]].type)) {
        // If x-axis values are datetime, set xAxis type to 'datetime'
        this.options.xAxis.type = 'datetime';
        // Set xAxis label format based on datatype
        const xAxisMeta = this.columnsMetaData[this.xAxis[0]];
        this.options.xAxis.labels = {
          format: `{value:${this.getToolTipDateFormat(xAxisMeta.type)}}`,
        };
      }
    }
  }

  protected override preprocessData() {
    // Remove existing data and series
    delete this.options.data;
    delete this.options.series;

    // Extract column configurations
    if (
      !this.data ||
      !Array.isArray(this.data) ||
      this.xAxis?.length === 0 ||
      this.yAxis?.length === 0
    ) {
      throw new Error('Invalid input: chartOptions and data are required.');
    }

    const groupKey = this.groupBy[0]; // e.g. "Agent Name"
    const xAxisTitle = this.xAxis[0];

    const yKeys = this.yAxis;

    const xKey = xAxisTitle;

    if (!xKey || !groupKey || yKeys.length === 0) {
      throw new Error(
        'Could not resolve key mappings from chartOptions and data.'
      );
    }

    // Unique group values (e.g., agent names)
    const groupSet = new Set();
    const xCategoriesSet = new Set();

    this.data.forEach((item) => {
      if (this.isDateTime(this.columnsMetaData[xKey].type)) {
        xCategoriesSet.add(
          this.parseDate(item[xKey], this.columnsMetaData[xKey].format)
        );
      } else {
        xCategoriesSet.add(item[xKey]);
      }
      groupSet.add(item[groupKey]);
    });

    const xCategories = Array.from(xCategoriesSet);
    const groups = Array.from(groupSet);

    const series: any = [];

    // For each metric (yKey) and each group (agent), create a series
    yKeys.forEach((yKey: any) => {
      groups.forEach((group: any) => {
        const dataArray = new Array(xCategories.length).fill(0);

        this.data.forEach((item: any) => {
          if (item[groupKey] === group) {
            const xIndex = xCategories.indexOf(
              this.isDateTime(this.columnsMetaData[this.xAxis[0]].type)
                ? this.parseDate(
                    item[xKey],
                    this.columnsMetaData[this.xAxis[0]].format
                  )
                : item[xKey]
            );
            const value = item[yKey] || 0;
            dataArray[xIndex] += value;
          }
        });
        series.push({
          name: `${group}`,
          yMetaData: this.columnsMetaData[yKey],
          xMetaData: this.columnsMetaData[xKey],
          data: dataArray,
          stack: yKey,
        });
      });
    });
    this.options.xAxis.categories = xCategories;
    this.options.series = series;
    this.addPlotOptions();
    this.addXAxisData();
    this.addToolTip();
  }
}
